/**
 * Lives, respawn and game over (GDD §4.6, §7.1; concept L1127–L1145, L1186–L1196). After a crash the wreck burns
 * for 2.8 s; the next jet then starts with a full tank and 2.6 s of blinking invulnerability. Under GDD rules it
 * starts at the checkpoint with the section rebuilt and the next bridge intact (Q2); concept rules respawn in place
 * (D9). With no jets left GAME OVER shows for 3.6 s, then the title returns with the attract demo (D10).
 */
import { config } from '../core/config.ts';
import { fract } from '../core/math.ts';
import { pushEvent, type GameSim } from '../sim/context.ts';
import { clearAroundJet, clearEnemies } from '../entities/enemies.ts';
import { clearShots } from '../entities/shots.ts';
import { resetBridge } from '../entities/bridge.ts';
import { beginRunSection, rebuildSection, setRunStart } from './checkpoint.ts';

/** The jet restarts on the river centreline 20 m ahead (concept L1132). */
const LINE_AHEAD = 20;
/** Concept rules: after hitting the bridge the jet backs off 170 m (concept L1129). */
const BACKOFF = 170;
/** The invulnerable jet blinks 6 times a second, shown 60% of each blink (concept L1187). */
const BLINK_RATE = 6;
const BLINK_ON = 0.6;

/** concept newGame (L1134–L1145); GDD rules also start a new run seed (D12) and extra-jet count (D11). */
export function newGame(sim: GameSim): void {
  const run = sim.run;
  const jet = sim.jet;
  clearEnemies(sim);
  clearShots(sim);
  sim.jetTrigCount = 0;
  run.level = run.firstLevel;
  run.firstLevel = 1;
  run.lives = config.lives.start;
  run.score = 0;
  run.fuel = 1;
  run.alive = true;
  run.invuln = 0;
  run.over = 0;
  run.overShown = false;
  run.title = config.flow.titleFadeSec;
  run.skill = Math.max(0.45, 0.86 - run.level * 0.04);
  run.jetVisible = true;
  run.nextExtra = config.lives.extraEvery;
  run.runs++;
  jet.x = sim.W.center(jet.z - LINE_AHEAD);
  if (sim.rules === 'gdd') {
    jet.vx = 0;
    run.runSeed = sim.nextRunSeed();
    sim.streams.beginRun(run.runSeed);
  }
  resetBridge(sim);
  beginRunSection(sim);
  pushEvent(sim, 'levelStart', run.level);
}

/** The next jet (concept respawn L1127–L1133 under concept rules; the checkpoint restart under GDD rules). */
export function respawn(sim: GameSim): void {
  const run = sim.run;
  const jet = sim.jet;
  run.alive = true;
  run.invuln = config.lives.invulnSec;
  run.fuel = 1;
  run.jetVisible = true;
  if (sim.rules === 'concept') {
    if (run.backoff) {
      jet.z += BACKOFF;
      run.backoff = false;
    }
    clearAroundJet(sim);
    clearShots(sim);
    jet.x = sim.W.center(jet.z - LINE_AHEAD);
  } else {
    clearEnemies(sim);
    clearShots(sim);
    jet.z = run.checkpointZ;
    jet.x = sim.W.center(jet.z - LINE_AHEAD);
    jet.vx = 0;
    jet.roll = 0;
    jet.yaw = 0;
    resetBridge(sim);
    rebuildSection(sim);
  }
  pushEvent(sim, 'respawn', run.level);
}

/** Start of a step (concept L1186–L1196): title fade, invulnerability blink, the wreck, respawn or game over. */
export function stepLives(sim: GameSim, dt: number): void {
  const run = sim.run;
  if (run.title > 0) run.title -= dt;
  if (run.invuln > 0) {
    run.invuln -= dt;
    run.jetVisible = run.alive && (run.invuln <= 0 || fract(run.invuln * BLINK_RATE) < BLINK_ON);
  }
  if (run.alive) return;
  run.deadT -= dt;
  if (run.deadT > 0) return;
  if (run.lives <= 0) {
    if (!run.overShown) {
      run.overShown = true;
      run.over = config.lives.gameOverSec;
      pushEvent(sim, 'gameOver', run.level);
    }
    run.over -= dt;
    if (run.over <= 0) {
      // GDD §4.6 and D10: after a player's GAME OVER the title returns, and with it the attract demo.
      if (run.player && sim.rules === 'gdd') {
        run.player = false;
        run.demo = true;
      }
      newGame(sim);
    }
  } else respawn(sim);
}

/**
 * The player takes over the title screen's demo (GDD §7.1; judgment calls 7 and 28): a new run with score 0, three
 * jets, a full tank and a new run seed, carrying on from the demo's jet, enemies and bridge, so nothing cuts or pops.
 * The 4.5 s title fade holds enemy fire and, for its first second, collisions. If the demo's jet is down, the
 * player's first jet starts at the checkpoint, as after any crash (Q2).
 */
export function takeOver(sim: GameSim): void {
  const run = sim.run;
  const l = config.lives;
  run.player = true;
  run.demo = false;
  run.firstLevel = 1;
  run.lives = l.start;
  run.score = 0;
  run.fuel = 1;
  run.over = 0;
  run.overShown = false;
  run.title = config.flow.titleFadeSec;
  run.nextExtra = l.extraEvery;
  run.runs++;
  run.runSeed = sim.nextRunSeed();
  sim.streams.beginRun(run.runSeed);
  if (run.alive) {
    setRunStart(sim);
    const b = sim.bridge;
    if (b.intact) b.hp = config.guns.bridgeHitsBase + config.guns.bridgeHitsPerLevel * run.level;
  } else {
    sim.jet.z = run.checkpointZ;
    setRunStart(sim);
    run.deadT = 0;
    respawn(sim);
  }
  pushEvent(sim, 'levelStart', run.level);
}

/** Pause menu, Restart: a new player run from the current checkpoint, a camera cut (judgment call 31). */
export function restartRun(sim: GameSim): void {
  freshRun(sim, true);
}

/** Pause menu, Quit: back to the title and its demo, from the current checkpoint (judgment call 6). */
export function quitRun(sim: GameSim): void {
  freshRun(sim, false);
}

function freshRun(sim: GameSim, player: boolean): void {
  const run = sim.run;
  const jet = sim.jet;
  run.player = player;
  run.demo = !player;
  jet.z = run.checkpointZ;
  jet.roll = 0;
  jet.yaw = 0;
  newGame(sim);
  pushEvent(sim, 'respawn', run.level);
}
